import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  categoryPath,
  fetchCatalog,
  fetchProductDetail,
  productPath,
  type CatalogProduct,
  type ProductDetailResponse
} from "../../lib/catalog";
import { formatCurrency, parseTestedWith, toSentenceCase } from "../../lib/utils";

type StockFilter = "all" | "in_stock" | "low_stock" | "out_of_stock";
type SortMode = "featured" | "price_asc" | "price_desc" | "newest" | "popular";

function stockTone(product: Pick<CatalogProduct, "inStock" | "quantity">) {
  if (!product.inStock || product.quantity <= 0) {
    return "border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300";
  }
  if (product.quantity <= 5) {
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300";
  }
  return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
}

function stockLabel(product: Pick<CatalogProduct, "inStock" | "quantity">) {
  if (!product.inStock || product.quantity <= 0) {
    return "Out of stock";
  }
  if (product.quantity <= 5) {
    return `Low stock (${product.quantity})`;
  }
  return `${product.quantity} in stock`;
}

export function SalesCatalogPage() {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [meta, setMeta] = useState({ totalProducts: 0, minPrice: 0, maxPrice: 0 });
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [stockFilter, setStockFilter] = useState<StockFilter>("all");
  const [sort, setSort] = useState<SortMode>("featured");
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summary = useMemo(() => {
    const visible = products.length;
    const inStock = products.filter((product) => product.inStock && product.quantity > 0).length;
    const lowStock = products.filter((product) => product.inStock && product.quantity > 0 && product.quantity <= 5).length;
    const valueBand =
      products.length > 0
        ? `${formatCurrency(Math.min(...products.map((product) => product.price)))} - ${formatCurrency(Math.max(...products.map((product) => product.price)))}`
        : formatCurrency(0);

    return { visible, inStock, lowStock, valueBand };
  }, [products]);

  useEffect(() => {
    let ignore = false;

    async function loadCatalog() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        if (categoryId) {
          params.set("categoryId", categoryId);
        }
        if (sort !== "featured") {
          params.set("sort", sort);
        }
        if (stockFilter === "in_stock") {
          params.set("inStock", "true");
        }

        const result = await fetchCatalog(params);
        let nextProducts = result.products;
        if (stockFilter === "low_stock") {
          nextProducts = nextProducts.filter((product) => product.inStock && product.quantity > 0 && product.quantity <= 5);
        } else if (stockFilter === "out_of_stock") {
          nextProducts = nextProducts.filter((product) => !product.inStock || product.quantity <= 0);
        }

        if (!ignore) {
          setProducts(nextProducts);
          setCategories(result.categories.map((category) => ({ id: category.id, name: category.name })));
          setMeta(result.meta);
          setSelectedProductId((current) => {
            if (nextProducts.length === 0) {
              return null;
            }
            if (current && nextProducts.some((product) => product.id === current)) {
              return current;
            }
            return nextProducts[0]?.id ?? null;
          });
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load the sales catalog.");
          setProducts([]);
          setSelectedProductId(null);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadCatalog();
    return () => {
      ignore = true;
    };
  }, [categoryId, query, sort, stockFilter]);

  useEffect(() => {
    let ignore = false;

    async function loadProduct() {
      if (!selectedProductId) {
        setSelectedProduct(null);
        return;
      }

      setLoadingDetail(true);
      try {
        const params = new URLSearchParams();
        params.set("id", String(selectedProductId));
        const result = await fetchProductDetail(params);
        if (!ignore) {
          setSelectedProduct(result);
        }
      } catch {
        if (!ignore) {
          setSelectedProduct(null);
        }
      } finally {
        if (!ignore) {
          setLoadingDetail(false);
        }
      }
    }

    void loadProduct();
    return () => {
      ignore = true;
    };
  }, [selectedProductId]);

  const detail = selectedProduct?.product ?? null;
  const compatibility = parseTestedWith(detail?.testedWith);

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sales catalog</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-nv-ink">Sell-ready catalog</h1>
        <p className="mt-4 max-w-3xl text-nv-slate">
          Search the active catalog, check stock pressure, and open product details fast while quoting or serving customers.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="admin-card rounded-[28px] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Visible products</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{summary.visible}</p>
        </div>
        <div className="admin-card rounded-[28px] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">In stock</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{summary.inStock}</p>
        </div>
        <div className="admin-card rounded-[28px] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Low stock</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{summary.lowStock}</p>
        </div>
        <div className="admin-card rounded-[28px] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Price band</p>
          <p className="mt-3 text-2xl font-semibold leading-tight text-nv-ink">{summary.valueBand}</p>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.9fr)]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <div className="admin-card rounded-[28px] p-5">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <input
                className="field-control px-4 py-3 text-sm xl:col-span-2"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by product name, category, or keywords"
                value={query}
              />
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setCategoryId(event.target.value)} value={categoryId}>
                <option value="">All categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={String(category.id)}>
                    {category.name}
                  </option>
                ))}
              </select>
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setStockFilter(event.target.value as StockFilter)} value={stockFilter}>
                <option value="all">All stock states</option>
                <option value="in_stock">In stock</option>
                <option value="low_stock">Low stock</option>
                <option value="out_of_stock">Out of stock</option>
              </select>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
              <div className="field-soft rounded-[22px] p-4 text-sm text-nv-slate">
                Catalog base: {meta.totalProducts} products · Price range {formatCurrency(meta.minPrice)} - {formatCurrency(meta.maxPrice)}
              </div>
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setSort(event.target.value as SortMode)} value={sort}>
                <option value="featured">Featured</option>
                <option value="newest">Newest</option>
                <option value="popular">Popular</option>
                <option value="price_asc">Price low to high</option>
                <option value="price_desc">Price high to low</option>
              </select>
            </div>
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {loading ? (
              <div className="admin-card rounded-[28px] p-5 text-sm text-nv-slate">Loading catalog...</div>
            ) : products.length > 0 ? (
              products.map((product) => (
                <button
                  className={`admin-card flex h-full flex-col rounded-[28px] p-5 text-left transition hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)] ${
                    selectedProductId === product.id ? "ring-1 ring-cyan-300/70 dark:ring-cyan-700/70" : ""
                  }`}
                  key={product.id}
                  onClick={() => setSelectedProductId(product.id)}
                  type="button"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="rounded-full border border-black/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-nv-slate dark:border-white/10">
                      {product.category}
                    </span>
                    <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold ${stockTone(product)}`}>
                      {stockLabel(product)}
                    </span>
                  </div>
                  <div className="mt-4 aspect-[4/3] overflow-hidden rounded-[22px] bg-black/[0.04] dark:bg-white/[0.04]">
                    {product.image ? (
                      <img alt={product.name} className="h-full w-full object-cover" src={product.image} />
                    ) : (
                      <div className="grid h-full place-items-center text-sm text-nv-slate">No image</div>
                    )}
                  </div>
                  <div className="mt-4 flex-1">
                    <p className="text-lg font-semibold tracking-tight text-nv-ink">{toSentenceCase(product.name)}</p>
                    <p className="mt-2 line-clamp-3 text-sm leading-6 text-nv-slate">{product.description || "No product summary available yet."}</p>
                  </div>
                  <div className="mt-4 flex items-end justify-between gap-3">
                    <div>
                      <p className="text-2xl font-semibold tracking-tight text-nv-ink">{formatCurrency(product.price)}</p>
                      <p className="mt-1 text-xs text-nv-slate">Product #{product.id}</p>
                    </div>
                    <span className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 dark:border-cyan-900/70 dark:bg-cyan-950/40 dark:text-cyan-300">
                      Open
                    </span>
                  </div>
                </button>
              ))
            ) : (
              <div className="admin-card rounded-[28px] p-5 text-sm text-nv-slate">No products matched the current view.</div>
            )}
          </div>
        </div>

        <aside className="min-h-0 overflow-y-auto pr-1">
          <div className="admin-card rounded-[32px] p-6">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Product detail</p>
            {loadingDetail ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Loading product detail...
              </div>
            ) : detail ? (
              <div className="mt-6 space-y-5">
                <div className="overflow-hidden rounded-[28px] bg-black/[0.04] dark:bg-white/[0.04]">
                  {detail.primaryImage ? (
                    <img alt={detail.name} className="h-[260px] w-full object-contain p-5" src={detail.primaryImage} />
                  ) : (
                    <div className="grid h-[260px] place-items-center text-sm text-nv-slate">Image coming soon</div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-black/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-nv-slate dark:border-white/10">
                    {detail.category}
                  </span>
                  <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold ${stockTone({ inStock: detail.inStock, quantity: detail.quantity })}`}>
                    {stockLabel({ inStock: detail.inStock, quantity: detail.quantity })}
                  </span>
                </div>

                <div>
                  <h2 className="text-3xl font-semibold tracking-tight text-nv-ink">{toSentenceCase(detail.name)}</h2>
                  <p className="mt-3 text-sm leading-7 text-nv-slate">{detail.description || "No product description available yet."}</p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Sale price</p>
                    <p className="mt-2 text-2xl font-semibold text-nv-ink">{formatCurrency(detail.price)}</p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Minimum price</p>
                    <p className="mt-2 text-2xl font-semibold text-nv-ink">
                      {typeof detail.minimumPrice === "number" && detail.minimumPrice > 0 ? formatCurrency(detail.minimumPrice) : "Not set"}
                    </p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">SKU</p>
                    <p className="mt-2 text-sm font-semibold text-nv-ink">{detail.sku || "Not recorded"}</p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Brand</p>
                    <p className="mt-2 text-sm font-semibold text-nv-ink">{detail.brand || "Not recorded"}</p>
                  </div>
                </div>

                {detail.specSummary ? (
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Sales summary</p>
                    <p className="mt-2 text-sm leading-7 text-nv-ink">{detail.specSummary}</p>
                  </div>
                ) : null}

                {compatibility.length > 0 ? (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Tested with</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {compatibility.map((item) => (
                        <span
                          className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 dark:border-cyan-900/70 dark:bg-cyan-950/40 dark:text-cyan-300"
                          key={item}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="grid gap-3">
                  <Link
                    className="brand-button inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-white"
                    to={productPath(detail.slug, detail.id)}
                  >
                    Open storefront product
                  </Link>
                  {detail.categoryId ? (
                    <Link
                      className="inline-flex items-center justify-center rounded-full border border-black/10 px-5 py-3 text-sm font-semibold text-nv-ink dark:border-white/10"
                      to={categoryPath(detail.categoryId, detail.category)}
                    >
                      Open category
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Select a product to inspect price, stock, compatibility, and quick links.
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
